import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  Link,
  Stack,
  TextField,
} from "@mui/material";
import Typography from "@mui/material/Typography";
import { makeStyles } from "@mui/styles";
import isEqual from "lodash.isequal";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { addBidChoiceEvent, fetchLatestBidChoiceEventByReferenceNumberAndEid } from "../../api/bid";
import { isUserAuthorizedByServer } from "../../api/userAccess";
import { selectUserSession } from "../../slices/userSession/userSessionSlice";
import { logout } from "../../util/authentication";
import { EMPLOYEE_BID_STATUS, BID_UPDATE_TYPE, BID_TYPE } from "../../util/bid/bidConstants";
import { buildBidChoiceEvent, prepareNewBidChoiceEventPayload } from "../../util/bid/bidEventBuilder";
import { getScrubbedBidEvent } from "../../util/bid/bidScrubber";
import { getBidName, getDisabledDatesBasedOnSchedule, getNonEmptyChoices } from "../../util/bid/timeOffBidUtils";

import {
  ADMIN_BID_CHOICE_CHAR_LIMIT,
  BREADCRUMBS_TITLE,
  CANCEL_WARNING_MESSAGE_ACTION_TYPE,
  EMPLOYEE_ID,
  FEATURES,
  HOME_PATH,
  STATION,
} from "../../util/constants.js";
import {
  BID_CHOICE_SAVE_ERROR_MESSAGE,
  BID_FORM_CANCEL_MESSAGE,
  BID_FORM_CLEAR_ALL,
  BID_FORM_CLEAR_ALL_CHOICES_MESSAGE,
  CHAR_LIMIT_MESSAGE,
  COMMENTS_REQUIRED,
  FREEDAY_BID_CHOICE_CONFIRMATION_MESSAGE,
  FREEDAY_BID_CHOICE_CONFIRMATION_TITLE,
  LAST_UPDATED,
  LAST_UPDATED_TIMESTAMP,
} from "../../util/messageConstants";
import { getStationTimeZoneDateTimeStringFromUTCInteger } from "../../util/timeZoneService";
import { getWorkgroupGivenJobCode } from "../../util/workgroupMapper";
import PageLoading from "../PageLoading";
import UnauthorizedMessage from "../UnauthorizedMessage";
import ViewCommentsDialog from "../ViewCommentsDialog";
import ConfirmButton from "../fundamentals/ConfirmButton";
import ConfirmationDialog from "../fundamentals/ConfirmationDialog";
import BidInfoDialog from "../fundamentals/BidInfoDialog";
import SlideUpTransition from "../fundamentals/SlideUpTransition";
import StarsBreadcrumbs from "../fundamentals/StarsBreadcrumbs";
import SuccessDialog from "../fundamentals/SuccessDialog";
import BidTimer from "./BidTimer";
import BidChoicesTable from "./BidChoicesTable";
import { fetchCurrentEmployeeMonthlyScheduleMap } from "../../api/scheduleEditor.js";
import { convertMonthStringToInteger } from "../../util/dateTimeFormatter.js";

const useStyles = makeStyles((theme) => ({
  inputText: {
    width: "15vw",
    textAlign: "left",
    paddingRight: "10px",
    "& .MuiInputBase-root": {
      background: "white",
    },
  },
  employeeDataTitle: {
    marginTop: "7px",
    color: theme.palette.secondary.swaGray,
  },
  employeeDataValue: {
    fontSize: "16px",
    marginTop: "-5px",
  },
  lastUpdated: {
    textAlign: "left",
    color: theme.palette.secondary.swaGray4,
    marginRight: "3.5vw",
  },
  commentBox: {
    width: "80%",
    margin: "10px auto",
  },
  addChoice: {
    margin: "15px",
  },
}));

const ManageFreedayBidChoices = (props) => {
  const classes = useStyles();
  const userSession = useSelector(selectUserSession);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isUserAuthorizedState, setIsUserAuthorizedState] = useState(false);
  const [isUserAuthorizedStateLoading, setIsUserAuthorizedStateLoading] = useState(true);

  // Bid Choice Data State
  const [bidChoiceData, setBidChoiceData] = useState(null);
  const [selectedChoices, setSelectedChoices] = useState([]);
  const [initialSelectedChoices, setInitialSelectedChoices] = useState([]);

  // Dialog state
  const [isClearAllDialogOpen, setIsClearAllDialogOpen] = useState(false);
  const [isExitDialogOpen, setIsExitDialogOpen] = useState(false);
  const [isGuideDialogOpen, setIsGuideDialogOpen] = useState(false);
  const [isSubmitDialogOpen, setIsSubmitDialogOpen] = useState(false);
  const [isSubmitResponseDialogOpen, setIsSubmitResponseDialogOpen] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [submitErrors, setSubmitErrors] = useState("");
  const [lastUpdatedByFirstName, setLastUpdatedByFirstName] = useState("");
  const [lastUpdatedByLastName, setLastUpdatedByLastName] = useState("");
  const [viewCommentsDialogOpen, setViewCommentsDialogOpen] = useState(false);
  const [lastUpdatedComments, setLastUpdatedComments] = useState("");
  const [comments, setComments] = useState("");
  const [commentsError, setCommentsError] = useState(false);
  const [commentErrorMessage, setCommentErrorMessage] = useState("");

  // Schedule state
  const [disabledDates, setDisabledDates] = useState([]);
  const [isScheduleLoading, setIsScheduleLoading] = useState(true);

  useEffect(() => {
    if (!isUserAuthorizedState) {
      isUserAuthorized();
    } else {
      if (props.selectedBidGroupEmployeeData) {
        fetchLatestFreedayBidChoices(props.selectedBidGroupEmployeeData.eid, props.referenceNumber);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserAuthorizedState, props.selectedBidGroupEmployeeData]);

  const isUserAuthorized = () => {
    isUserAuthorizedByServer(userSession).then((is200) => {
      if (is200) {
        setIsUserAuthorizedState(true);
      } else {
        logout();
      }
      setIsUserAuthorizedStateLoading(false);
    });
  };

  useEffect(() => {
    if (bidChoiceData) {
      fetchSchedule(bidChoiceData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bidChoiceData]);

  const fetchSchedule = ({ eid, station, schedYear, schedMonth }) => {
    if (eid && station && schedYear && schedMonth) {
      setIsScheduleLoading(true);
      fetchCurrentEmployeeMonthlyScheduleMap(eid, station, schedYear, convertMonthStringToInteger(schedMonth))
        .then((res) => res.json())
        .then((response) => {
          if (response?.event) {
            const disabledDates = getDisabledDatesBasedOnSchedule({
              scheduledMap: response.event,
              station: station,
              bidType: BID_TYPE.FREEDAY,
            });
            setDisabledDates(disabledDates);
            setIsScheduleLoading(false);
          } else {
            throw Error();
          }
        })
        .catch((err) => {
          setIsScheduleLoading(false);
          console.error(`Bid Error: %j`, err);
        });
    }
  };

  const handleCommentsChange = (comments) => {
    setCommentsError(false);
    setCommentErrorMessage("");
    setComments(comments.target.value);
  };

  const fetchLatestFreedayBidChoices = (eid, referenceNumber) => {
    fetchLatestBidChoiceEventByReferenceNumberAndEid(eid, referenceNumber)
      .then((res) => res.json())
      .then((response) => {
        if (response?.event) {
          const bidChoiceEvent = getScrubbedBidEvent(response.event);
          setBidChoiceData(bidChoiceEvent);
          setSelectedChoices(response.event?.choices?.length > 0 ? response.event.choices : [""]);
          setInitialSelectedChoices(response.event?.choices?.length > 0 ? response.event.choices : [""]);
          if (response.event?.modifiedByFirstName && response.event?.modifiedByLastName) {
            setLastUpdatedByFirstName(response.event?.modifiedByFirstName);
            setLastUpdatedByLastName(response.event?.modifiedByLastName);
          }

          setLastUpdatedComments(response.event?.comments);
          setIsPageLoading(false);
        } else {
          throw Error();
        }
      })
      .catch((err) => {
        console.error(`Bid Error: %j`, err);
        setIsPageLoading(false);
      });
  };

  const handleSubmitClicked = () => {
    if (comments === "") {
      setCommentsError(true);
      setCommentErrorMessage(COMMENTS_REQUIRED);
    } else if (comments.length > ADMIN_BID_CHOICE_CHAR_LIMIT) {
      setCommentsError(true);
      setCommentErrorMessage(CHAR_LIMIT_MESSAGE(ADMIN_BID_CHOICE_CHAR_LIMIT));
    } else {
      setIsSubmitLoading(true);
      console.log("Saving bid choice data...");
      let payload = buildBidChoiceEvent(
        prepareNewBidChoiceEventPayload({ bidChoiceData, selectedChoices }),
        userSession,
        comments
      );
      addBidChoiceEvent(payload)
        .then((res) => res.json())
        .then((result) => {
          if (result.message) {
            console.error("Error: ", result.message);
            setSubmitErrors(result.message);
            setIsSubmitLoading(false);
            setIsSubmitDialogOpen(false);
            setIsSubmitResponseDialogOpen(true);
          } else {
            console.log("Success!");
            props.setIsBidGroupTabChanged(true);
            setIsSubmitDialogOpen(false);
            setIsSubmitLoading(false);
            setIsSubmitResponseDialogOpen(true);
          }
        })
        .catch((err) => {
          console.log("err", err);
          setIsSubmitDialogOpen(false);
          setIsSubmitLoading(false);
        });
    }
  };

  const handleClickSubmitDialogOpen = () => {
    setSelectedChoices(getNonEmptyChoices(selectedChoices));
    setIsSubmitDialogOpen(true);
  };

  const handleClickSubmitDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    setIsSubmitDialogOpen(false);
  };

  const handleClickCancelDialogOpen = () => {
    if (!isEqual(getNonEmptyChoices(selectedChoices), getNonEmptyChoices(initialSelectedChoices))) {
      setIsExitDialogOpen(true);
    } else {
      // no changes made, so move away from manage bid choices page
      props.setIsManageFreedayBidChoicesSelected(false);
    }
  };

  const handleClickCancelDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    setIsExitDialogOpen(false);
  };

  const handleClickGuideDialogOpen = () => {
    setIsGuideDialogOpen(true);
  };

  const handleCloseClickedGuideDialog = () => {
    setIsGuideDialogOpen(false);
  };

  const handleClickClearAllDialogOpen = () => {
    setIsClearAllDialogOpen(true);
  };

  const handleClickCancelClearAllDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    setIsClearAllDialogOpen(false);
  };

  const handleClearAllChoices = () => {
    setSelectedChoices([""]);
    setIsClearAllDialogOpen(false);
  };

  const handleAddChoice = () => {
    const existingChoices = [...selectedChoices];
    setSelectedChoices([...existingChoices, ""]);
    setTimeout(() => {
      const rowElem = document.querySelector(`[data-id="${selectedChoices.length + 1}"]`);
      if (rowElem) {
        rowElem.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }, 0);
  };

  const renderComments = () => {
    return (
      <>
        <TextField
          className={classes.commentBox}
          value={comments}
          variant="outlined"
          onChange={handleCommentsChange}
          label="Comments (Mandatory)"
          multiline
          error={commentsError}
          helperText={commentErrorMessage}
        />

        {comments.length > ADMIN_BID_CHOICE_CHAR_LIMIT ? (
          <Box sx={{ textAlign: "right", color: "primary.swaRed", width: "90%" }}>
            {comments.length} / {ADMIN_BID_CHOICE_CHAR_LIMIT}
          </Box>
        ) : (
          <Box sx={{ textAlign: "right", width: "90%" }}>
            {comments.length} / {ADMIN_BID_CHOICE_CHAR_LIMIT}
          </Box>
        )}
      </>
    );
  };

  const renderSubmitFreedayBidChoiceDialog = () => {
    return (
      <Dialog
        fullWidth
        maxWidth="sm"
        sx={{ margin: "auto" }}
        open={isSubmitDialogOpen}
        TransitionComponent={SlideUpTransition}
        keepMounted
      >
        {selectedChoices?.length === 0 ? (
          <>
            <DialogTitle>Submit a Blank Bid</DialogTitle>
            <DialogContent>
              <DialogContentText component={"div"}>
                <Grid container>
                  <div style={{ marginTop: "10px" }}>You are submitting a blank bid.</div>
                  <div style={{ marginTop: "10px" }}>Any choices previously submitted will be overriden.</div>
                </Grid>
              </DialogContentText>
            </DialogContent>
            {renderComments()}
          </>
        ) : (
          <>
            <DialogTitle>Submit Bid Choices</DialogTitle>
            <DialogContent>
              <DialogContentText component={"div"}>
                <Grid container>
                  <div style={{ marginTop: "10px" }}>
                    You are submitting choices for{" "}
                    <b>
                      {selectedChoices?.length} day{selectedChoices?.length > 1 && "s"}
                    </b>
                    .
                  </div>
                  <div style={{ marginTop: "10px" }}>Any choices previously submitted will be overriden.</div>
                  <div style={{ marginTop: "10px" }}>
                    You will see your submitted choices the next time you open this bid form again.
                  </div>
                </Grid>
              </DialogContentText>
            </DialogContent>
            {renderComments()}
          </>
        )}
        <DialogActions>
          <ConfirmButton variant="outlined" color="primary" onClick={handleClickSubmitDialogClose}>
            Cancel
          </ConfirmButton>
          <ConfirmButton variant="contained" color="primary" onClick={handleSubmitClicked} isLoading={isSubmitLoading}>
            Submit
          </ConfirmButton>
        </DialogActions>
      </Dialog>
    );
  };

  const getLastSubmittedTimestamp = () => {
    return bidChoiceData?.createdAt ? Date.parse(bidChoiceData.createdAt) : "";
  };

  const getBidAdminPath = () => {
    return props.featureConfig.childItems.find((childItem) => childItem.key === FEATURES.BID_ADMINISTRATION).path;
  };

  const getLastUpdatedTimeString = () => {
    return getStationTimeZoneDateTimeStringFromUTCInteger(getLastSubmittedTimestamp(), bidChoiceData.station).toFormat(
      "MM/dd/yyyy T ZZZZ"
    );
  };

  const renderNavBtnsOfBidForm = () => {
    return (
      <>
        {props.isAwardedEmpReadOnlyView ? (
          <>
            <Box sx={{ paddingRight: "10px", paddingTop: "10px" }}>
              <Button
                sx={{
                  width: "100px",
                }}
                variant="outlined"
                color="primary"
                onClick={props.handleManageFreedayBidBackClick}
              >
                Back
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Box sx={{ paddingRight: "10px", paddingTop: "10px" }}>
              <Button sx={{ width: "100px" }} variant="outlined" color="primary" onClick={handleClickCancelDialogOpen}>
                Cancel
              </Button>
            </Box>
            <Box sx={{ paddingRight: "10px", paddingTop: "10px" }}>
              <Button
                sx={{
                  width: "100px",
                  backgroundColor: "secondary.swaGray",
                  "&:hover": {
                    backgroundColor: "secondary.swaGray5",
                  },
                }}
                variant="contained"
                onClick={handleClickGuideDialogOpen}
              >
                Guide
              </Button>
            </Box>
            <Box sx={{ paddingTop: "10px" }}>
              <Button
                sx={{ width: "100px" }}
                variant="contained"
                color="primary"
                disabled={
                  bidChoiceData?.updateType === BID_UPDATE_TYPE.DELETE ||
                  bidChoiceData?.status === EMPLOYEE_BID_STATUS.WITHDRAWN ||
                  isEqual(getNonEmptyChoices(selectedChoices), getNonEmptyChoices(initialSelectedChoices))
                }
                onClick={handleClickSubmitDialogOpen}
              >
                Submit
              </Button>
            </Box>
          </>
        )}
      </>
    );
  };

  const breadcrumbsList = [
    BREADCRUMBS_TITLE.HOME,
    BREADCRUMBS_TITLE.BID_ADMINISTRATION,
    `${BREADCRUMBS_TITLE.MANAGE_FREE_DAY_BID} / ${props.selectedBidGroupEmployeeData.eid}`,
  ];
  const breadcrumbsLinks = [HOME_PATH, getBidAdminPath()];

  const renderFreedayBidChoicesPage = () => {
    if (isPageLoading || isScheduleLoading) {
      return <PageLoading />;
    } else {
      return (
        <>
          <StarsBreadcrumbs
            breadcrumbsList={breadcrumbsList}
            breadcrumbsLinks={breadcrumbsLinks}
            isConfirmationRequired={
              !isEqual(getNonEmptyChoices(selectedChoices), getNonEmptyChoices(initialSelectedChoices))
            }
            cancelActionType={CANCEL_WARNING_MESSAGE_ACTION_TYPE.EDITS}
          />
          {/* Title & Cancel/Submit Buttons */}
          <Grid container>
            <Grid container justifyContent="space-between">
              <Grid item sx={{ paddingLeft: "2rem", paddingTop: "10px" }}>
                <Grid item>
                  <Typography variant="h5" component="div" align="left">
                    {getBidName(bidChoiceData)} Freeday Bid
                  </Typography>
                  {getLastSubmittedTimestamp() !== "" &&
                  lastUpdatedByFirstName &&
                  lastUpdatedByLastName &&
                  lastUpdatedByFirstName !== "" &&
                  lastUpdatedByLastName !== "" ? (
                    <Grid className={classes.lastUpdated}>
                      {`${LAST_UPDATED}${lastUpdatedByLastName}, ${lastUpdatedByFirstName} ${getLastUpdatedTimeString()} `}
                      {lastUpdatedComments !== "" ? (
                        <Link onClick={() => setViewCommentsDialogOpen(true)} component="button" variant="subtitle2">
                          (View comment)
                        </Link>
                      ) : (
                        ""
                      )}
                    </Grid>
                  ) : getLastSubmittedTimestamp() !== "" ? (
                    <Grid className={classes.lastUpdated}>
                      {`${LAST_UPDATED_TIMESTAMP} ${getLastUpdatedTimeString()} `}
                    </Grid>
                  ) : (
                    ""
                  )}

                  <Box sx={{ textAlign: "left", paddingTop: "10px" }}>
                    {bidChoiceData &&
                      bidChoiceData.windowStartDate !== null &&
                      bidChoiceData.windowEndDate !== null && (
                        <Typography variant="body2" sx={{ color: "primary.swaRed", fontSize: "16px" }}>
                          <BidTimer
                            windowDates={[bidChoiceData.windowStartDate, bidChoiceData.windowEndDate]}
                            station={bidChoiceData.station}
                          />
                        </Typography>
                      )}
                  </Box>
                </Grid>
              </Grid>

              <Stack>
                <Grid item sx={{ display: "flex", paddingRight: "2rem" }}>
                  {renderNavBtnsOfBidForm()}
                </Grid>
              </Stack>
            </Grid>
            {props.selectedBidGroupEmployeeData && (
              <Grid
                container
                sx={{
                  pt: 3,
                  paddingLeft: "2rem",
                }}
                spacing={2}
              >
                <Grid item md={4}>
                  <div style={{ display: "flex" }}>
                    <div>
                      <TextField
                        style={{ maxWidth: "17vw", margin: "auto", textAlign: "left", width: "90px" }}
                        label={STATION}
                        variant="outlined"
                        value={props.station}
                        disabled
                      />
                    </div>
                    <div style={{ paddingLeft: "10px" }}>
                      <TextField
                        className={classes.inputText}
                        label={EMPLOYEE_ID}
                        variant="outlined"
                        value={props.selectedBidGroupEmployeeData?.eid}
                        disabled
                      />
                    </div>
                    <div>
                      <Button style={{ padding: "15px" }} variant="contained" color="primary" disabled>
                        View
                      </Button>
                    </div>
                  </div>
                </Grid>
                <Grid
                  item
                  md={8}
                  sx={{
                    paddingRight: "3.2rem",
                  }}
                >
                  {props.selectedBidGroupEmployeeData ? (
                    <div>
                      <Grid
                        container
                        sx={{
                          backgroundColor: "secondary.swaGray1",
                          height: "auto",
                          borderRadius: "5px",
                          marginLeft: "20px",
                        }}
                      >
                        <Grid
                          item
                          xs={4}
                          style={{ fontSize: "24px", paddingTop: "11px", paddingLeft: "16px", textAlign: "left" }}
                        >
                          {props.selectedBidGroupEmployeeData.lastName +
                            ", " +
                            props.selectedBidGroupEmployeeData.shortName}
                        </Grid>
                        <Grid item xs={8} sx={{ textAlign: "left", maxHeight: "56px" }}>
                          <Stack direction="row">
                            <Box ml={2} mr={2}>
                              <Typography className={classes.employeeDataTitle} variant="overline" component="span">
                                HOME STATION
                              </Typography>
                              <p className={classes.employeeDataValue}>
                                {props.selectedBidGroupEmployeeData.homeStation}
                              </p>
                            </Box>
                            <Box ml={2} mr={2}>
                              <Typography className={classes.employeeDataTitle} variant="overline" component="span">
                                WORKGROUP
                              </Typography>
                              <p className={classes.employeeDataValue}>
                                {getWorkgroupGivenJobCode(props.selectedBidGroupEmployeeData.homeAccountJobCode)}
                              </p>
                            </Box>
                            <Box ml={2} mr={2}>
                              <Typography className={classes.employeeDataTitle} variant="overline" component="span">
                                STATUS
                              </Typography>
                              <p className={classes.employeeDataValue}>{props.selectedBidGroupEmployeeData.ftpt}</p>
                            </Box>
                          </Stack>
                        </Grid>
                      </Grid>
                    </div>
                  ) : (
                    ""
                  )}
                </Grid>
              </Grid>
            )}
            <Box
              sx={{
                width: "100%",
                margin: "auto",
                paddingLeft: "2rem",
                paddingRight: "2rem",
                marginTop: "1rem",
              }}
            >
              <>
                <Grid
                  container
                  sx={{
                    border: "solid 1px",
                    borderColor: "secondary.swaGray2",
                    backgroundColor: "secondary.swaGray2",
                    paddingLeft: "1rem",
                  }}
                >
                  <Grid item xs={6} sx={{ display: "flex", justifyContent: "start", paddingTop: "6px" }}>
                    {props.isAwardedEmpReadOnlyView ? (
                      <Typography sx={{ fontWeight: "bold" }}>
                        Review your {getNonEmptyChoices(selectedChoices).length} choice
                        {getNonEmptyChoices(selectedChoices).length > 1 ? "s" : ""}
                      </Typography>
                    ) : (
                      <Typography sx={{ fontWeight: "bold" }}>
                        {getNonEmptyChoices(selectedChoices).length}{" "}
                        {` Choice${getNonEmptyChoices(selectedChoices).length > 1 ? "s" : ""}`} Entered
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={6} sx={{ display: "flex", justifyContent: "end", paddingRight: "8px" }}>
                    <Button
                      sx={{ fontWeight: "bold", textDecoration: "underline" }}
                      onClick={handleClickClearAllDialogOpen}
                      disabled={props.isAwardedEmpReadOnlyView}
                    >
                      Clear All
                    </Button>
                    <ConfirmationDialog
                      isDialogOpen={isClearAllDialogOpen}
                      title={BID_FORM_CLEAR_ALL}
                      message={BID_FORM_CLEAR_ALL_CHOICES_MESSAGE}
                      handleNoButtonClicked={handleClickCancelClearAllDialogClose}
                      handleYesButtonClicked={handleClearAllChoices}
                      noTitle="Cancel"
                      yesTitle="Clear All"
                    />
                  </Grid>
                </Grid>

                <Grid
                  container
                  sx={{
                    border: "solid 1px",
                    borderColor: "secondary.swaGray2",
                    borderBottom: 0,
                  }}
                >
                  <Box sx={{ width: "inherit" }}>
                    {
                      <BidChoicesTable
                        selectedChoices={selectedChoices}
                        setSelectedChoices={setSelectedChoices}
                        isDesktopView={true}
                        isAdmin={true}
                        isBidChoicesLoading={isPageLoading}
                        bidChoiceData={bidChoiceData}
                        disabledDates={disabledDates}
                        isReadOnly={props.isAwardedEmpReadOnlyView}
                        selectedAwardedBid={props.selectedAwardedBid}
                        bidType={bidChoiceData.bidType}
                      />
                    }
                    <Divider />
                    <Button
                      className={classes.addChoice}
                      variant="contained"
                      onClick={handleAddChoice}
                      disabled={
                        (selectedChoices.some((choice) => choice === "") ? true : false) ||
                        props.isAwardedEmpReadOnlyView
                      }
                    >
                      Add Another Choice
                    </Button>
                    <Divider />
                  </Box>
                </Grid>
              </>
            </Box>

            {/* Dialogs */}
            <ConfirmationDialog
              isDialogOpen={isExitDialogOpen}
              title="Cancel"
              message={BID_FORM_CANCEL_MESSAGE}
              handleNoButtonClicked={handleClickCancelDialogClose}
              handleYesButtonClicked={() => props.setIsManageFreedayBidChoicesSelected(false)}
            />
            {renderSubmitFreedayBidChoiceDialog()}
            {viewCommentsDialogOpen && (
              <ViewCommentsDialog
                isDialogOpen={viewCommentsDialogOpen}
                handleClickDialogClose={() => setViewCommentsDialogOpen(false)}
                header={`${LAST_UPDATED}${lastUpdatedByLastName}, ${lastUpdatedByFirstName} on `}
                publishedTimestamp={getLastUpdatedTimeString()}
                message={lastUpdatedComments}
              />
            )}
            <BidInfoDialog
              isDialogOpen={isGuideDialogOpen}
              title="Guide"
              handleCloseClicked={handleCloseClickedGuideDialog}
              isDesktopView={props.isDesktopView}
              eid={userSession.eid}
              bidChoiceData={bidChoiceData}
              lastSubmittedTimestamp={getLastSubmittedTimestamp()}
            />
            <SuccessDialog
              isSuccessDialogOpen={isSubmitResponseDialogOpen}
              title={FREEDAY_BID_CHOICE_CONFIRMATION_TITLE}
              message={FREEDAY_BID_CHOICE_CONFIRMATION_MESSAGE}
              hasError={submitErrors !== ""}
              error={BID_CHOICE_SAVE_ERROR_MESSAGE}
              handleOKClick={() => {
                setIsSubmitResponseDialogOpen(false);
                props.setIsManageFreedayBidChoicesSelected(false);
              }}
            />
          </Grid>
        </>
      );
    }
  };

  if (!isUserAuthorizedState && !isUserAuthorizedStateLoading) {
    return <UnauthorizedMessage />;
  }

  return (
    <div className="App-contents enable-scroll" data-testid="manage-freeday-bid-choices-page">
      {isUserAuthorizedStateLoading ? <PageLoading /> : renderFreedayBidChoicesPage()}
    </div>
  );
};

export default ManageFreedayBidChoices;
